import { isFactorTv } from "@rnv/renative";
import { apiRequest } from "./client";
import {
  DEMO_VIDEO_LIST_ITEMS,
  getDemoVideo,
  isDemoVideoId,
} from "../data/demoVideos";

export type VideoListItem = {
  id: string;
  title: string;
  thumbUrl: string;
  duration: number;
  ageMin: number;
  ageMax: number;
  categories: string[];
  isPremium: boolean;
};

export type VideoDetail = VideoListItem & {
  hlsUrl: string;
  questions: Question[];
  // Container of the stored media (e.g. "video/mp4", "audio/mpeg"). Native
  // playback needs it to pick the URL extension AVFoundation requires.
  videoMimeType?: string | null;
  // Long-form blurb shown on the video screen. The backend has always sent it
  // (VIDEO_DETAIL_SELECT) — it was just missing from this type, so no screen
  // rendered it. Null when nobody has written one.
  description?: string | null;
};

// Each answer option carries a label and (optionally) an icon key that the
// renderer resolves through `STUB_ICONS` (or whatever live extraction API
// replaces it later). Legacy questions in the DB are `string[]`; readers
// normalize both shapes, so the type accepts either here.
//
// `imageUrl` is the newer "object pick" mode: the parent attaches a
// VideoEntity image to an answer, and the TV renderer shows the picture
// instead of (or alongside) the text label. Forward-compatible with the
// existing iconKey path — a question may mix text-only, icon-only, and
// image-backed options.
export type QuestionOption = {
  label: string;
  iconKey?: string;
  imageUrl?: string;
};

// TV-side render mode for a question. "row" is the default; "corners"
// requires 4 options each with an `iconKey` set.
export type QuestionLayout = "row" | "corners";

export type Question = {
  id: string;
  videoId: string;
  tSec: number;
  prompt: string;
  options: Array<QuestionOption | string>;
  correct: number;
  layout?: QuestionLayout;
  // Optional scene illustration shown above the answer tiles (e.g. the
  // social-emotional "Benny's tower fell over" picture). Absolute URL or a
  // data: URI.
  imageUrl?: string | null;
};

function filterDemoByCategories(
  items: VideoListItem[],
  categoriesCsv?: string,
): VideoListItem[] {
  if (!categoriesCsv) return items;
  const wanted = categoriesCsv
    .split(",")
    .map((c) => c.trim().toLowerCase())
    .filter(Boolean);
  if (wanted.length === 0) return items;
  return items.filter((v) =>
    v.categories.some((c) => wanted.includes(c.toLowerCase())),
  );
}

export const contentApi = {
  list: async (
    params: {
      ageMin?: number;
      ageMax?: number;
      categories?: string;
      skip?: number;
      take?: number;
    },
    token: string,
  ): Promise<{ videos: VideoListItem[]; total: number }> => {
    // Build query string by hand — RN-tvos's runtime doesn't reliably
    // polyfill URLSearchParams, and the previous `new URLSearchParams()`
    // call threw synchronously on Android, causing the whole
    // Promise.allSettled to reject before the request even fired.
    const parts: string[] = [];
    if (params.ageMin !== undefined)
      parts.push(`ageMin=${encodeURIComponent(String(params.ageMin))}`);
    if (params.ageMax !== undefined)
      parts.push(`ageMax=${encodeURIComponent(String(params.ageMax))}`);
    if (params.categories)
      parts.push(`categories=${encodeURIComponent(params.categories)}`);
    if (params.skip !== undefined)
      parts.push(`skip=${encodeURIComponent(String(params.skip))}`);
    if (params.take !== undefined)
      parts.push(`take=${encodeURIComponent(String(params.take))}`);
    const qs = parts.length ? `?${parts.join("&")}` : "";

    let backend: { videos: VideoListItem[]; total: number } = {
      videos: [],
      total: 0,
    };
    try {
      backend = await apiRequest<{ videos: VideoListItem[]; total: number }>(
        `/v1/content${qs}`,
        { token },
      );
    } catch {
      // Network or auth hiccup — fall through to demo content so the kid
      // screen isn't empty.
    }

    // TV (tvOS / Android TV / Tizen / webOS) hides premium content entirely:
    // there's no in-living-room way to subscribe, so a locked card is dead
    // weight. Web + mobile keep showing premium (with a lock + subscribe CTA).
    if (backend.videos.length > 0) {
      if (!isFactorTv) return backend;
      const free = backend.videos.filter((v) => !v.isPremium);
      return { videos: free, total: free.length };
    }

    const demo = filterDemoByCategories(
      DEMO_VIDEO_LIST_ITEMS,
      params.categories,
    );
    const demoVisible = isFactorTv ? demo.filter((v) => !v.isPremium) : demo;
    return { videos: demoVisible, total: demoVisible.length };
  },

  get: (id: string, token: string): Promise<VideoDetail> => {
    if (isDemoVideoId(id)) {
      const demo = getDemoVideo(id);
      if (demo) return Promise.resolve(demo);
    }
    return apiRequest<VideoDetail>(`/v1/content/${id}`, { token });
  },
};

// Unauthenticated "free library" — drives the signed-out guest browse + play
// experience. Hits /v1/public/videos, which the backend hard-scopes to
// public + non-premium + finished videos, so nothing private/paywalled leaks.
// No token and (deliberately) no demo fallback: it shows real opted-in
// content, and an empty list just renders the empty state.
export const publicContentApi = {
  list: (
    params: { categories?: string; skip?: number; take?: number } = {},
  ): Promise<{ videos: VideoListItem[]; total: number }> => {
    const parts: string[] = [];
    if (params.categories)
      parts.push(`categories=${encodeURIComponent(params.categories)}`);
    if (params.skip !== undefined)
      parts.push(`skip=${encodeURIComponent(String(params.skip))}`);
    if (params.take !== undefined)
      parts.push(`take=${encodeURIComponent(String(params.take))}`);
    const qs = parts.length ? `?${parts.join("&")}` : "";
    return apiRequest<{ videos: VideoListItem[]; total: number }>(
      `/v1/public/videos${qs}`,
    );
  },

  get: (id: string): Promise<VideoDetail> =>
    apiRequest<VideoDetail>(`/v1/public/videos/${id}`),
};

// Coerce mixed legacy/structured option arrays into the structured shape the
// admin endpoint expects. Strings become `{ label }`; objects pass through
// (with `iconKey` and/or `imageUrl` preserved when set).
export function toQuestionOptionPayload(
  opts: Array<QuestionOption | string>,
): QuestionOption[] {
  return opts.map((o) =>
    typeof o === "string"
      ? { label: o }
      : {
          label: o.label,
          ...(o.iconKey ? { iconKey: o.iconKey } : {}),
          ...(o.imageUrl ? { imageUrl: o.imageUrl } : {}),
        },
  );
}

export type CreateQuestionInput = {
  tSec: number;
  prompt: string;
  options: Array<QuestionOption | string>;
  correct: number;
  layout?: "row" | "corners";
};

export type UpdateQuestionInput = Partial<CreateQuestionInput>;

// Wan job status, mirrored verbatim. Studio renders friendly per-state
// labels (see GEN_STATUS_LABEL in WebStudio / StudioScreen).
export type GenerationStatus =
  | "queued"
  | "moderating"
  | "generating"
  | "stitching"
  | "done"
  | "failed"
  | "cancelled";

// One row of the `VideoPublication` join — surfaces "this video is
// published for this kid". The `listMyGenerations` endpoint includes this
// so Studio can render a "Published for Leo, Mila" badge per card without
// a second round-trip.
export type VideoPublication = {
  childId: string;
  publishedAt: string;
};

// A Video row with generation-pipeline fields surfaced. `hlsUrl` is empty
// until `generationStatus === 'done'`. Frontend should treat empty `hlsUrl`
// as "not yet playable" and gate the open-player action on it.
export type GeneratedVideo = {
  id: string;
  title: string;
  hlsUrl: string;
  thumbUrl: string;
  ageMin: number;
  ageMax: number;
  duration: number;
  categories: string[];
  isPremium: boolean;
  // In the public "free library" — guests can browse + play it without an
  // account. Owner-controlled via the Studio "Make public" toggle.
  isPublic: boolean;
  // Admin review for sharing a parent's video publicly. 'none' = never asked,
  // 'pending' = waiting on the admin (still private), 'approved' = now public,
  // 'rejected' = declined (reviewNote says why). Optional for back-compat with
  // servers that predate the review flow.
  reviewStatus?: "none" | "pending" | "approved" | "rejected";
  reviewedAt?: string | null;
  reviewNote?: string | null;
  // Parent-authored long-form copy. Null when the parent hasn't filled it
  // in yet (every freshly-generated video starts that way).
  description: string | null;
  createdAt: string;
  generationJobId: string | null;
  generationStatus: GenerationStatus;
  generationError: string | null;
  generationPrompt: string | null;
  parentId: string | null;
  // Present on `listMyGenerations` / `publishVideo` / `unpublishVideo`
  // responses. Empty array means the parent hasn't published the video for
  // any of their kids yet. Absent on the immediate /generate response
  // (which doesn't include the join).
  publications?: VideoPublication[];
};

// Admin wrappers — keep `{label, iconKey}` flowing all the way to the
// controller. Both the create and update endpoints already accept the
// structured option shape (see `admin-content.controller.ts`).
export const adminContentApi = {
  createQuestion: (
    videoId: string,
    input: CreateQuestionInput,
    token: string,
  ): Promise<Question> =>
    apiRequest<Question>(`/v1/admin/videos/${videoId}/questions`, {
      method: "POST",
      body: { ...input, options: toQuestionOptionPayload(input.options) },
      token,
    }),

  updateQuestion: (
    questionId: string,
    input: UpdateQuestionInput,
    token: string,
  ): Promise<Question> =>
    apiRequest<Question>(`/v1/admin/videos/questions/${questionId}`, {
      method: "PATCH",
      body: {
        ...input,
        ...(input.options
          ? { options: toQuestionOptionPayload(input.options) }
          : {}),
      },
      token,
    }),

  generateVideo: (
    body: {
      prompt: string;
      photoDataUrl?: string;
      // 5-30s; backend clamps.
      duration?: number;
      // '3d_cartoon' | '2d_cartoon' | 'storybook' | 'claymation'.
      // Backend falls back to 'storybook' if missing / unknown.
      style?: string;
      // false → skip the /prompts/extend pre-pass and send the parent's
      // raw prompt straight to /generate. Default behavior is to enrich.
      extendPrompt?: boolean;
    },
    token: string,
  ): Promise<GeneratedVideo> =>
    apiRequest<GeneratedVideo>(`/v1/admin/videos/generate`, {
      method: "POST",
      body,
      token,
    }),

  // Upload a finished video instead of generating one. The file rides in as a
  // `data:video/mp4;base64,...` string, stored in Postgres; the returned row
  // behaves like a finished generation (status 'done').
  uploadVideo: (
    body: {
      dataUrl: string;
      title?: string;
      categories?: string[];
      ageMin?: number;
      ageMax?: number;
      duration?: number;
    },
    token: string,
  ): Promise<GeneratedVideo> =>
    apiRequest<GeneratedVideo>(`/v1/admin/videos/upload`, {
      method: "POST",
      body,
      token,
    }),

  getGeneration: (jobId: string, token: string): Promise<GeneratedVideo> =>
    apiRequest<GeneratedVideo>(`/v1/admin/videos/generation/${jobId}`, {
      token,
    }),

  cancelGeneration: (jobId: string, token: string): Promise<GeneratedVideo> =>
    apiRequest<GeneratedVideo>(`/v1/admin/videos/generation/${jobId}/cancel`, {
      method: "POST",
      token,
    }),

  // Soft-delete: archive hides the video everywhere (Studio, kids, public) and
  // schedules permanent deletion after the retention window; unarchive restores
  // it within that window.
  archiveVideo: (
    videoId: string,
    token: string,
  ): Promise<{ id: string; archivedAt: string | null }> =>
    apiRequest(`/v1/admin/videos/${videoId}/archive`, {
      method: "POST",
      token,
    }),

  unarchiveVideo: (
    videoId: string,
    token: string,
  ): Promise<{ id: string; archivedAt: string | null }> =>
    apiRequest(`/v1/admin/videos/${videoId}/unarchive`, {
      method: "POST",
      token,
    }),

  // AI-generated poster candidates for a video (upstream image generation,
  // costs credits). Returns small JPEG data URLs — pass the chosen one to
  // uploadVideoThumb to set it as the poster.
  posterSuggestions: (
    videoId: string,
    count: number,
    token: string,
  ): Promise<{ posters: string[] }> =>
    apiRequest(`/v1/admin/videos/${videoId}/poster-suggestions`, {
      method: "POST",
      body: { count },
      token,
    }),

  // ── Share with everyone: parent submits, admin decides ────────────────
  // Submitting does NOT publish — it queues the video for admin review and it
  // stays private until approved. Only `approveReview` makes it public.
  submitForReview: (videoId: string, token: string): Promise<GeneratedVideo> =>
    apiRequest<GeneratedVideo>(`/v1/admin/videos/${videoId}/submit-review`, {
      method: "POST",
      token,
    }),

  withdrawReview: (videoId: string, token: string): Promise<GeneratedVideo> =>
    apiRequest<GeneratedVideo>(`/v1/admin/videos/${videoId}/withdraw-review`, {
      method: "POST",
      token,
    }),

  // Admin (super-admin) only.
  reviewQueue: (token: string): Promise<GeneratedVideo[]> =>
    apiRequest<GeneratedVideo[]>(`/v1/admin/videos/review-queue`, { token }),

  approveReview: (videoId: string, token: string): Promise<GeneratedVideo> =>
    apiRequest<GeneratedVideo>(`/v1/admin/videos/${videoId}/review/approve`, {
      method: "POST",
      token,
    }),

  rejectReview: (
    videoId: string,
    note: string | undefined,
    token: string,
  ): Promise<GeneratedVideo> =>
    apiRequest<GeneratedVideo>(`/v1/admin/videos/${videoId}/review/reject`, {
      method: "POST",
      body: { note },
      token,
    }),

  // Lists the calling parent's own generations (in-flight + finished + failed).
  // Used by Studio to repopulate the gen-card grid after a page refresh and
  // by the parent Library to show "Generating…" placeholders.
  listMyGenerations: (token: string): Promise<GeneratedVideo[]> =>
    apiRequest<GeneratedVideo[]>(`/v1/admin/videos/generations`, { token }),

  // Publish one of the parent's own generated videos for a specific set of
  // kids. Idempotent at the backend — re-publishing for the same child is a
  // no-op. Returns the video with its updated `publications` array so the
  // caller can re-render the "Published for…" badge without re-fetching.
  publishVideo: (
    videoId: string,
    childIds: string[],
    token: string,
  ): Promise<GeneratedVideo> =>
    apiRequest<GeneratedVideo>(`/v1/admin/videos/${videoId}/publications`, {
      method: "POST",
      body: { childIds },
      token,
    }),

  // Reverse of `publishVideo`. Removing all kids effectively unpublishes the
  // video — the kid Library will stop showing it on the next fetch.
  unpublishVideo: (
    videoId: string,
    childIds: string[],
    token: string,
  ): Promise<GeneratedVideo> =>
    apiRequest<GeneratedVideo>(`/v1/admin/videos/${videoId}/publications`, {
      method: "DELETE",
      body: { childIds },
      token,
    }),

  // Patch parent-editable details on a generated video. All fields optional;
  // pass only what changed. Returns the refreshed video with its publications
  // included so the caller can merge it straight back into local state.
  updateVideo: (
    videoId: string,
    body: Partial<{
      title: string;
      description: string | null;
      categories: string[];
      ageMin: number;
      ageMax: number;
      isPublic: boolean;
    }>,
    token: string,
  ): Promise<GeneratedVideo> =>
    apiRequest<GeneratedVideo>(`/v1/admin/videos/${videoId}`, {
      method: "PATCH",
      body,
      token,
    }),

  // Upload a poster image (data URL). 2 MB cap, PNG/JPEG/WebP. Returns the
  // newly-served URL so the FE can update its local Generation immediately.
  // The backend also rewrites `Video.thumbUrl` to point at the same URL, so
  // any list endpoint that returns `thumbUrl` starts showing the poster on
  // the next refresh.
  uploadVideoThumb: (
    videoId: string,
    dataUrl: string,
    token: string,
  ): Promise<{ ok: boolean; thumbUrl: string }> =>
    apiRequest<{ ok: boolean; thumbUrl: string }>(
      `/v1/admin/videos/${videoId}/thumb`,
      { method: "POST", body: { dataUrl }, token },
    ),

  // Clear the uploaded poster. The FE falls back to its procedural card art
  // afterward (handled in WebStudio / WebHome).
  removeVideoThumb: (
    videoId: string,
    token: string,
  ): Promise<{ ok: boolean }> =>
    apiRequest<{ ok: boolean }>(`/v1/admin/videos/${videoId}/thumb`, {
      method: "DELETE",
      token,
    }),

  deleteQuestion: (questionId: string, token: string): Promise<void> =>
    apiRequest<void>(`/v1/admin/videos/questions/${questionId}`, {
      method: "DELETE",
      token,
    }),
};
